import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  TextField,
  Autocomplete,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import {
  AGE_GROUP_VALUES,
  COLOR_PALETTE_VALUES,
  GENDER_VALUES,
  INTENSITY_LEVEL_VALUES,
  PERFORMANCE_CONTEXT_VALUES,
  type AgeGroup,
  type ColorPalette,
  type Gender,
  type IntensityLevel,
  type PerformanceContext,
} from '../../contracts/openapi/assetContracts';
import {
  ageGroupLabels,
  colorPaletteLabels,
  genderLabels,
  intensityLevelLabels,
  performanceContextLabels,
} from '../../contracts/openapi/assetEnumLabels';
import { environmentSafeLog } from '../../utils/environment';
import { StarsLayerMetadata } from '../../types/layerMetadata';
import {
  extractStringValue,
  isEmpty,
} from '../../utils/starMetadataUtils';
import { API_STRING_MAX_LENGTH, STAR_METADATA } from '../../constants/apiMetadataConstraints';
import {
  buildEnumOptions,
  coerceEnumArray,
  coerceEnumValue,
} from './enumFieldUtils';

interface StarsMetadataEditorProps {
  layerMetadata: StarsLayerMetadata;
  isEditable?: boolean;
  onMetadataChange?: (updatedMetadata: StarsLayerMetadata) => void;
  // Legacy support during migration
  asset?: any;
  // 🎯 INHERITANCE FALLBACK SUPPORT - Base asset data for client-side fallback
  baseAssetData?: any;
  // Layout option for view mode
  twoColumnLayout?: boolean;
  // AI-generated description for fallback pattern (Issue #15)
  aiGeneratedDescription?: string;
}

/**
 * Apply intelligent defaults for Stars layer to prevent null/undefined/empty values
 * This ensures all fields have sensible defaults to avoid backend validation errors
 *
 * Based on successful Songs layer implementation - provides smart fallbacks
 * when AI doesn't detect values or returns null/empty fields
 */
const applyIntelligentStarsDefaults = (data: any) => {
  // Start with existing data, then apply defaults for any null/undefined values
  const defaults = {
    // Core star information
    starName: 'Unknown Star',
    realName: '',

    // Identity characteristics with intelligent defaults
    gender: 'unknown',
    ageGroup: 'adult', // Will be overridden by taxonomy-aware logic below
    ethnicity: '', // Keep empty - sensitive field, user should specify

    // Physical traits with realistic defaults
    physicalTraits: {
      bodyType: '', // Keep empty - personal/sensitive
      height: '', // Keep empty - specific measurement
      hairColor: '', // AI should detect this, keep empty if not
      hairLength: '', // AI should detect this, keep empty - no default to avoid override
      hairStyle: '', // AI should detect this, keep empty - no default to avoid override
      eyeColor: '', // AI should detect this, keep empty if not
      skinTone: '', // AI should detect this, keep empty if not
    },

    // Performance characteristics
    archetype: 'performer', // Generic default
    musicalStyle: [], // Keep empty array - genre specific
    careerStage: 'emerging', // Common starting point
    energy: 'medium', // Neutral default
    personalityTraits: [], // Keep empty - specific to individual
    vibe: 'neutral', // Safe default

    // Makeup and styling (enhanced defaults)
    makeupType: 'natural', // Most common default
    colorPalette: 'neutral', // Safe default palette
    hairTexture: 'straight', // Common default
    hairTreatment: 'natural',
    hairAccessories: '',
    eyeMakeup: 'minimal',
    lipMakeup: 'natural',
    faceMakeup: 'light',
    makeupEffects: 'none',

    // V2.0 compatibility
    layerType: 'stars',
  };

  // Apply defaults only for null, undefined values (NOT empty strings)
  const result = { ...data };

  // Handle physicalTraits as nested object
  if (!result.physicalTraits) {
    result.physicalTraits = {};
  }

  // TAXONOMY-AWARE AGE GROUP: Override ageGroup default based on subcategory context
  // This should match the user's taxonomy selection (e.g., S.GRL.YOU = young_adult)
  if (
    result.ageGroup === null ||
    result.ageGroup === undefined ||
    result.ageGroup === 'adult'
  ) {
    // Try to get subcategory from global context or passed data
    const subcategory = data.subcategory || data.taxonomyContext?.subcategory;
    if (subcategory) {
      const taxonomyAwareAgeGroup = getTaxonomyAwareAgeGroup('S', subcategory);
      if (taxonomyAwareAgeGroup !== 'adult') {
        // Only override if different from default
        result.ageGroup = taxonomyAwareAgeGroup;
        console.log(
          `⭐ [STARS TAXONOMY-AWARE] ageGroup: S.*.${subcategory} → ${taxonomyAwareAgeGroup}`
        );
      }
    }
  }

  // INTELLIGENT DEFAULTS SYSTEM - Enhanced Priority-based approach

  // Create a priority system for value sources (following comprehensive strategy)
  const getFieldValue = (field: string, currentValue: any) => {
    // Priority 1: Explicit AI-extracted value (including empty string if AI set it explicitly)
    if (currentValue !== null && currentValue !== undefined) {
      return { value: currentValue, source: 'ai' };
    }

    // Priority 2: Base asset inheritance (if variant)
    if (result.assetType === 'variant' && result.baseStarId) {
      return { value: null, source: 'inheritance-pending' };
    }

    // Priority 3: Minimal defaults only when no AI data exists
    return { value: null, source: 'minimal-default' };
  };

  if (result.assetType === 'variant') {
    console.log(
      '🎯 [BASE INHERITANCE] Variant detected, checking for base asset inheritance'
    );

    // Check if baseStarId is empty and try to auto-populate it
    if (!result.baseStarId) {
      console.log(
        '🎯 [AUTO BASE DETECTION] No baseStarId found, attempting auto-detection'
      );

      // In a full implementation, we could make an API call here to find
      // the most suitable base asset with same taxonomy
      console.log(
        '⚠️  [BASE INHERITANCE] Missing baseStarId for variant - base asset selection required'
      );
      console.log(
        '💡 [SUGGESTION] User should select base asset via BaseVariantSelector component'
      );

      // For now, ensure fields are initialized as empty arrays for potential inheritance
      const inheritableFields = [
        'musicalStyle',
        'performanceContext',
        'personalityTraits',
        'targetAudience',
        'culturalContext',
      ];

      inheritableFields.forEach(field => {
        if (
          !result[field] ||
          (Array.isArray(result[field]) && result[field].length === 0)
        ) {
          if (Array.isArray(result[field])) {
            console.log(
              `🎯 [INHERITANCE PREP] ${field}: initialized for future inheritance`
            );
          }
        }
      });
    } else {
      console.log(
        '🎯 [BASE INHERITANCE] Base asset ID found:',
        result.baseStarId
      );

      // Fields that should inherit from base asset for variants
      const inheritableFields = [
        'musicalStyle',
        'performanceContext',
        'personalityTraits',
        'targetAudience',
        'culturalContext',
      ];

      inheritableFields.forEach(field => {
        if (
          !result[field] ||
          (Array.isArray(result[field]) && result[field].length === 0)
        ) {
          console.log(
            `🎯 [BASE INHERITANCE] ${field}: ready for inheritance from baseStarId: ${result.baseStarId}`
          );
          // In a full implementation, we would fetch and apply base asset data here
          // This would require an async API call to fetch the base asset's metadata
        }
      });
    }
  }

  // Priority 3: Context-aware defaults (gender, taxonomy)
  // Priority 4: System defaults (only for truly empty fields)

  Object.keys(defaults).forEach(key => {
    if (key === 'physicalTraits') {
      // Handle nested physicalTraits object with enhanced priority system
      Object.keys(defaults.physicalTraits).forEach(traitKey => {
        const currentValue = result.physicalTraits[traitKey];
        const fieldName = `physicalTraits.${traitKey}`;

        // Apply enhanced priority-based value resolution
        const valueDecision = getFieldValue(fieldName, currentValue);

        if (valueDecision.source === 'ai') {
          // AI value already exists, preserve it completely
          // No need to modify - value already exists
        } else if (valueDecision.source === 'inheritance-pending') {
          // Don't apply defaults, wait for base asset inheritance
        } else if (valueDecision.source === 'smart-default') {
          // Apply intelligent context-aware defaults
          let intelligentDefault = '';

          // Context-aware defaults based on other fields
          if (traitKey === 'hairLength' && result.gender) {
            intelligentDefault =
              result.gender === 'Female' ? 'Medium' : 'Short';
            console.log(
              `🎯 [CONTEXT DEFAULT] ${traitKey}: ${intelligentDefault} (based on gender: ${result.gender}) - NO AI VALUE DETECTED`
            );
          } else if (traitKey === 'makeupType' && result.gender) {
            intelligentDefault =
              result.gender === 'Female' ? 'Natural' : 'Minimal';
            console.log(
              `🎯 [CONTEXT DEFAULT] ${traitKey}: ${intelligentDefault} (based on gender: ${result.gender}) - NO AI VALUE DETECTED`
            );
          } else if (traitKey === 'eyeColor') {
            intelligentDefault = 'Brown'; // Most common globally
            console.log(
              `🎯 [STATISTICAL DEFAULT] ${traitKey}: ${intelligentDefault} (global statistical norm) - NO AI VALUE DETECTED`
            );
          } else if (traitKey === 'skinTone') {
            intelligentDefault = 'Medium'; // Neutral default
            console.log(
              `🎯 [NEUTRAL DEFAULT] ${traitKey}: ${intelligentDefault} (neutral baseline) - NO AI VALUE DETECTED`
            );
          } else {
            // Fall back to original defaults only if needed
            const fallbackDefault =
              defaults.physicalTraits[
                traitKey as keyof typeof defaults.physicalTraits
              ];
            if (fallbackDefault !== '') {
              intelligentDefault = fallbackDefault;
              console.log(
                `⭐ [SYSTEM DEFAULT] ${traitKey}: ${intelligentDefault} (system fallback) - NO AI VALUE DETECTED`
              );
            }
          }

          if (intelligentDefault !== '') {
            result.physicalTraits[traitKey] = intelligentDefault;
          }
        }
      });
    } else {
      // Handle top-level fields with enhanced priority system
      const currentValue = result[key];
      const valueDecision = getFieldValue(key, currentValue);

      if (valueDecision.source === 'ai') {
        // AI value already exists, preserve it completely
        // No need to modify - value already exists
      } else if (valueDecision.source === 'inheritance-pending') {
        // Don't apply defaults, wait for base asset inheritance
      } else if (valueDecision.source === 'smart-default') {
        let intelligentDefault = '';

        // Context-aware top-level defaults
        if (key === 'energy') {
          intelligentDefault = 'Medium'; // Neutral default
          console.log(
            `🎯 [PERFORMANCE DEFAULT] ${key}: ${intelligentDefault} (neutral performance level)`
          );
        } else if (key === 'vibe') {
          intelligentDefault = 'Neutral'; // Matches energy
          console.log(
            `🎯 [PERFORMANCE DEFAULT] ${key}: ${intelligentDefault} (neutral vibe)`
          );
        } else if (
          key === 'musicalStyle' &&
          Array.isArray(result[key]) &&
          result[key].length === 0
        ) {
          // Context-aware musical style defaults based on taxonomy
          const contextualStyles =
            data.subcategory === 'POP'
              ? ['Pop']
              : data.subcategory === 'HIP'
              ? ['Hip-Hop']
              : data.subcategory === 'RNB'
              ? ['R&B']
              : data.subcategory === 'JAZ'
              ? ['Jazz']
              : data.subcategory === 'ROC'
              ? ['Rock']
              : ['Pop']; // Default fallback
          result[key] = contextualStyles;
          console.log(
            `🎯 [MUSICAL CONTEXT DEFAULT] ${key}: ${JSON.stringify(
              contextualStyles
            )} (based on subcategory: ${data.subcategory || 'unknown'})`
          );
          return; // Skip the normal string handling below
        } else if (
          key === 'personalityTraits' &&
          Array.isArray(result[key]) &&
          result[key].length === 0
        ) {
          // Neutral personality defaults
          result[key] = ['Friendly', 'Confident'];
          console.log(
            `🎯 [PERSONALITY DEFAULT] ${key}: ["Friendly", "Confident"] (neutral personality traits)`
          );
          return; // Skip the normal string handling below
        } else if (
          key === 'performanceContext' &&
          Array.isArray(result[key]) &&
          result[key].length === 0
        ) {
          // Performance context defaults
          result[key] = ['Studio', 'Live Performance'];
          console.log(
            `🎯 [PERFORMANCE DEFAULT] ${key}: ["Studio", "Live Performance"] (general performance contexts)`
          );
          return; // Skip the normal string handling below
        } else {
          // Fall back to original defaults
          const fallbackDefault = defaults[key as keyof typeof defaults];
          if (
            fallbackDefault !== '' &&
            fallbackDefault !== null &&
            fallbackDefault !== undefined
          ) {
            // Only apply if it's a string type (not array or object)
            if (typeof fallbackDefault === 'string') {
              intelligentDefault = fallbackDefault;
              console.log(
                `⭐ [SYSTEM DEFAULT] ${key}: ${intelligentDefault} (system fallback)`
              );
            } else if (Array.isArray(fallbackDefault)) {
              // Handle array defaults
              result[key] = fallbackDefault;
              console.log(
                `⭐ [SYSTEM DEFAULT] ${key}: ${JSON.stringify(
                  fallbackDefault
                )} (array fallback)`
              );
            }
          }
        }

        if (intelligentDefault !== '') {
          result[key] = intelligentDefault;
        }
      }
    }
  });

  // Summary log of intelligent defaults application (reduced verbosity)
  if (result.assetType === 'variant') {
    console.log(
      '🎯 [DEFAULTS] Variant asset processed with inheritance support'
    );
  }

  return result;
};

/**
 * Get taxonomy-aware age group based on subcategory (matches backend logic)
 */
const getTaxonomyAwareAgeGroup = (
  layer: string,
  subcategory?: string
): string => {
  if (layer !== 'S' || !subcategory) {
    return 'adult'; // Default fallback
  }

  // Taxonomy-aware age group mapping based on NNA taxonomy
  const ageGroupMapping = {
    // Children subcategories
    CHI: 'child',
    GIR: 'child',
    BOY: 'child',

    // Teen/Young subcategories
    YOU: 'young_adult', // S.GRL.YOU should map to young_adult, not child
    TEE: 'teen',
    TEN: 'teen',

    // Adult subcategories
    ADU: 'adult',
    WOM: 'adult',
    MAN: 'adult',
    MAL: 'adult',
    FEM: 'adult',

    // Mature subcategories
    MAT: 'mature',
    SEN: 'senior',
    ELD: 'senior',
  };

  return (
    ageGroupMapping[subcategory as keyof typeof ageGroupMapping] || 'adult'
  );
};

const StarsMetadataEditor: React.FC<StarsMetadataEditorProps> = ({
  layerMetadata,
  asset,
  isEditable = false,
  twoColumnLayout = false,
  onMetadataChange,
  baseAssetData,
  aiGeneratedDescription,
}) => {
  // V2.0 ARCHITECTURE: Use layerMetadata first, with legacy fallback
  const starMetadata = React.useMemo(() => {
    let rawMetadata = {};

    // Priority 1: V2.0 layerMetadata prop (preferred)
    if (layerMetadata) {
      environmentSafeLog('📌 [STARS V2.0] Using layerMetadata prop');
      rawMetadata = layerMetadata;
    }
    // Legacy support during migration
    else if (asset) {
      environmentSafeLog(
        '🔎 [STARS LEGACY] Extracting from legacy asset structure'
      );

      // Priority 2: AI Contract layerMetadata (from AI Response Contract)
      if (asset.aiMetadata?.layerMetadata && asset.layer === 'S') {
        environmentSafeLog('📌 [STARS LEGACY] Using aiMetadata.layerMetadata');
        rawMetadata = asset.aiMetadata.layerMetadata;
      }
      // Priority 3: Root level starMetadata (current frontend structure)
      else if (asset.starMetadata) {
        environmentSafeLog('📌 [STARS LEGACY] Using root starMetadata');
        rawMetadata = asset.starMetadata;
      }
      // Priority 4: Root level starsMetadata (legacy AI-generated)
      else if (asset.starsMetadata) {
        environmentSafeLog('📌 [STARS LEGACY] Using root starsMetadata');
        rawMetadata = asset.starsMetadata;
      }
      // Priority 5: AI metadata structure (nested legacy)
      else if (asset.aiMetadata?.starsMetadata) {
        environmentSafeLog('📌 [STARS LEGACY] Using aiMetadata.starsMetadata');
        rawMetadata = asset.aiMetadata.starsMetadata;
      }
      // Priority 6: Legacy metadata structure
      else if (asset.metadata?.starsMetadata) {
        environmentSafeLog('📌 [STARS LEGACY] Using metadata.starsMetadata');
        rawMetadata = asset.metadata.starsMetadata;
      } else {
        environmentSafeLog('⚠️ [STARS V2.0] No star metadata found in asset');
      }
    }

    // INTELLIGENT DEFAULTS: Apply smart defaults to handle null/missing values
    const processedMetadata = applyIntelligentStarsDefaults(rawMetadata);

    environmentSafeLog(
      '⭐ [STARS INTELLIGENT DEFAULTS] Applied smart defaults to Stars metadata',
      { original: rawMetadata, processed: processedMetadata }
    );

    return processedMetadata;
  }, [layerMetadata, asset]);

  // 🎯 INHERITANCE FALLBACK UI - Get display value with base asset fallback
  const getDisplayValue = useCallback(
    (fieldName: string) => {
      const currentValue = starMetadata[fieldName];
      const baseValue =
        baseAssetData?.starMetadata?.[fieldName] ||
        baseAssetData?.starsMetadata?.[fieldName];

      // Use current value if exists, otherwise use inherited value
      if (Array.isArray(currentValue)) {
        if (currentValue.length === 0 && baseValue) {
          const inheritedArray = Array.isArray(baseValue)
            ? baseValue
            : [baseValue];
          console.log(
            `🔄 [INHERITANCE FALLBACK] ${fieldName}: Using inherited value [${inheritedArray.join(
              ', '
            )}] from base asset`
          );
          return inheritedArray;
        }
        return currentValue;
      }

      if (isEmpty(currentValue) && baseValue) {
        const inheritedValue = extractStringValue(baseValue);
        console.log(
          `🔄 [INHERITANCE FALLBACK] ${fieldName}: Using inherited value "${inheritedValue}" from base asset`
        );
        return inheritedValue;
      }

      return currentValue;
    },
    [starMetadata, baseAssetData]
  );

  // 🎯 INHERITANCE INDICATOR - Check if field value is inherited
  const isInheritedValue = useCallback(
    (fieldName: string) => {
      const currentValue = starMetadata[fieldName];
      const baseValue =
        baseAssetData?.starMetadata?.[fieldName] ||
        baseAssetData?.starsMetadata?.[fieldName];

      // Return true if current is empty but base has value
      if (Array.isArray(currentValue)) {
        return (
          currentValue.length === 0 &&
          baseValue &&
          (Array.isArray(baseValue) ? baseValue.length > 0 : !!baseValue)
        );
      }

      return isEmpty(currentValue) && !!baseValue;
    },
    [starMetadata, baseAssetData]
  );

  // State for editable fields - initialize with extracted metadata
  const [editableData, setEditableData] = React.useState(() => {
    console.log(
      '🚀 [STARS INFO INIT] Initializing editableData state with starMetadata:',
      starMetadata
    );

    // CRITICAL FIX (Issue #15): Auto-populate description with AI-generated description
    // Pattern: AI description → creator description → empty string
    const initialDescription =
      aiGeneratedDescription ||
      starMetadata?.creatorDescription ||
      starMetadata?.description ||
      '';
    if (aiGeneratedDescription && !starMetadata?.creatorDescription && !starMetadata?.description) {
      console.log('📝 [STARS INFO] Auto-populating Star Description with AI-generated description:', aiGeneratedDescription);
    }

    return {
      creatorDescription: initialDescription,
      starName: starMetadata?.starName || '',
      realName: starMetadata?.realName || '',
      gender: coerceEnumValue(starMetadata?.gender, GENDER_VALUES),
      ageGroup: coerceEnumValue(
        starMetadata?.ageGroup || starMetadata?.age,
        AGE_GROUP_VALUES
      ),
      ethnicity: starMetadata?.ethnicity || '',
      physicalTraits: {
        bodyType:
          starMetadata?.physicalTraits?.bodyType ||
          starMetadata?.bodyType ||
          '',
        height:
          starMetadata?.physicalTraits?.height || starMetadata?.height || '',
        hairColor: (() => {
          // INTELLIGENT HAIR COLOR DETECTION: Auto-detect from tags when AI missed it
          const aiHairColor = starMetadata?.physicalTraits?.hairColor || starMetadata?.hairColor || '';
          const allTags = [
            ...(starMetadata?.tags || []),
            ...(asset?.tags || []),
            ...(asset?.aiMetadata?.tags || [])
          ];

          // If AI already detected a specific color, use it
          if (aiHairColor && aiHairColor !== 'Other' && aiHairColor !== '') {
            return aiHairColor;
          }

          // Auto-detect colorful hair from tags
          const hasColorfulHair = allTags.some(tag =>
            tag.toLowerCase().includes('colorful') ||
            tag.toLowerCase().includes('blonde-highlight') ||
            tag.toLowerCase().includes('highlighted-hair') ||
            tag.toLowerCase().includes('multi-color') ||
            tag.toLowerCase().includes('rainbow')
          );

          if (hasColorfulHair) {
            console.log('🎨 [HAIR COLOR] Auto-detected colorful hair from tags:', allTags.filter(tag =>
              tag.toLowerCase().includes('colorful') ||
              tag.toLowerCase().includes('highlight') ||
              tag.toLowerCase().includes('multi') ||
              tag.toLowerCase().includes('rainbow')
            ));
            return 'Colorful';
          }

          // Check for other specific colors in tags
          const hasBlonde = allTags.some(tag => tag.toLowerCase().includes('blonde') && !tag.toLowerCase().includes('highlight'));
          if (hasBlonde) return 'Blonde';

          const hasBrown = allTags.some(tag => tag.toLowerCase().includes('brown'));
          if (hasBrown) return 'Medium Brown';

          const hasAuburn = allTags.some(tag => tag.toLowerCase().includes('auburn'));
          if (hasAuburn) return 'Auburn';

          // Default to AI response or empty
          return aiHairColor || '';
        })(),
        hairLength:
          starMetadata?.physicalTraits?.hairLength ||
          starMetadata?.hairLength ||
          '',
        hairStyle:
          starMetadata?.physicalTraits?.hairStyle ||
          starMetadata?.hairStyle ||
          '',
        eyeColor: (() => {
          const rawEyeColor =
            starMetadata?.physicalTraits?.eyeColor ||
            starMetadata?.eyeColor ||
            '';
          const normalizedEyeColor = rawEyeColor
            ? rawEyeColor.charAt(0).toUpperCase() +
              rawEyeColor.slice(1).toLowerCase()
            : '';
          const validOptions = [
            'Brown',
            'Blue',
            'Green',
            'Hazel',
            'Gray',
            'Other',
          ];
          return validOptions.includes(normalizedEyeColor)
            ? normalizedEyeColor
            : rawEyeColor;
        })(),
        skinTone:
          starMetadata?.physicalTraits?.skinTone ||
          starMetadata?.skinTone ||
          '',
      },
      archetype: starMetadata?.archetype || '',
      musicalStyle: (() => {
        // CRITICAL FIX: Check multiple data sources for musical style
        // IMPORTANT: Check for non-empty arrays, not just truthy (empty array [] is truthy!)
        // Priority: starMetadata prop > asset.starMetadata > asset.aiMetadata.layerMetadata > root
        let musicalStyleData =
          (starMetadata?.musicalStyle?.length > 0 ? starMetadata.musicalStyle : null) || // From starMetadata prop
          asset?.starMetadata?.musicalStyle || // From asset.starMetadata (PRIORITY)
          asset?.aiMetadata?.layerMetadata?.musicalStyle || // From AI layerMetadata
          asset?.musicalStyle || // From root asset level (fallback)
          asset?.aiMetadata?.starsMetadata?.musicalStyle || // From AI metadata (legacy)
          [];

        console.log('🎵 [MUSICAL STYLE DEBUG] Sources checked:', {
          starMetadata: starMetadata?.musicalStyle,
          assetStarMetadata: asset?.starMetadata?.musicalStyle,
          assetAILayerMetadata: asset?.aiMetadata?.layerMetadata?.musicalStyle,
          assetRoot: asset?.musicalStyle,
          assetAIMetadata: asset?.aiMetadata?.starsMetadata?.musicalStyle,
          finalValue: musicalStyleData,
        });

        return Array.isArray(musicalStyleData)
          ? musicalStyleData
          : typeof musicalStyleData === 'string' && musicalStyleData
          ? musicalStyleData.split(',').map((s: string) => s.trim())
          : [];
      })(),
      careerStage: starMetadata?.careerStage || '',
      energy: coerceEnumValue(
        starMetadata?.energy || starMetadata?.energyLevel,
        INTENSITY_LEVEL_VALUES
      ),
      personalityTraits: Array.isArray(starMetadata?.personalityTraits)
        ? starMetadata?.personalityTraits
        : typeof starMetadata?.personalityTraits === 'string' &&
          starMetadata?.personalityTraits
        ? starMetadata.personalityTraits.split(',').map((t: string) => t.trim())
        : [],
      vibe: starMetadata?.vibe || '',
      // AlgoRhythm Metadata (REQUIRED for recommendation engine)
      performanceContext: coerceEnumArray(
        starMetadata?.performanceContext,
        PERFORMANCE_CONTEXT_VALUES,
        STAR_METADATA.performanceContext.maxItems
      ),
      targetAudience: coerceEnumArray(
        starMetadata?.targetAudience,
        AGE_GROUP_VALUES,
        STAR_METADATA.targetAudience.maxItems
      ),
      // CRITICAL FIX: Add missing makeup fields that were causing Step 4 issues
      makeupType:
        starMetadata?.makeupType || starMetadata?.makeupStyle?.type || '',
      colorPalette: coerceEnumValue(
        starMetadata?.colorPalette || starMetadata?.makeupStyle?.palette,
        COLOR_PALETTE_VALUES
      ),
      // ✨ Enhanced Hair & Makeup Fields
      hairTexture: starMetadata?.hairTexture || '',
      hairTreatment: (() => {
        // INTELLIGENT HAIR TREATMENT DETECTION: Auto-detect from tags when AI missed it
        const aiHairTreatment = starMetadata?.hairTreatment || '';
        const allTags = [
          ...(starMetadata?.tags || []),
          ...(asset?.tags || []),
          ...(asset?.aiMetadata?.tags || [])
        ];

        // If AI already detected a specific treatment, use it
        if (aiHairTreatment && aiHairTreatment !== 'Natural' && aiHairTreatment !== 'None') {
          return aiHairTreatment;
        }

        // Auto-detect highlights from tags
        const hasHighlights = allTags.some(tag => {
          const lowerTag = tag.toLowerCase();
          return (
            lowerTag.includes('highlight') ||
            lowerTag.includes('blonde-highlight') ||
            lowerTag.includes('highlighted-hair') ||
            lowerTag.includes('golden-highlight') ||
            lowerTag.includes('golden highlight') ||
            (lowerTag.includes('golden') && lowerTag.includes('highlight'))
          );
        });

        if (hasHighlights) {
          const highlightTags = allTags.filter(tag => {
            const lowerTag = tag.toLowerCase();
            return (
              lowerTag.includes('highlight') ||
              lowerTag.includes('golden-highlight') ||
              lowerTag.includes('golden highlight') ||
              (lowerTag.includes('golden') && lowerTag.includes('highlight'))
            );
          });
          console.log('🎨 [HAIR TREATMENT] Auto-detected highlights from tags:', highlightTags);
          return 'Highlights';
        }

        // Check for other hair treatments in tags
        const hasBalayage = allTags.some(tag => tag.toLowerCase().includes('balayage'));
        if (hasBalayage) return 'Balayage';

        const hasOmbre = allTags.some(tag => tag.toLowerCase().includes('ombre') || tag.toLowerCase().includes('ombré'));
        if (hasOmbre) return 'Ombré';

        const hasBleached = allTags.some(tag => tag.toLowerCase().includes('bleach') || tag.toLowerCase().includes('platinum'));
        if (hasBleached) return 'Bleached';

        // Default to AI response or Natural
        return aiHairTreatment || '';
      })(),
      hairAccessories: starMetadata?.hairAccessories || '',
      eyeMakeup: starMetadata?.eyeMakeup || '',
      lipMakeup: starMetadata?.lipMakeup || '',
      faceMakeup: starMetadata?.faceMakeup || '',
      makeupEffects: starMetadata?.makeupEffects || '',
      // CRITICAL FIX: Preserve form's existing variant data instead of defaulting to 'base'
      assetType: starMetadata?.assetType || layerMetadata?.assetType || 'base',
      baseStarId: starMetadata?.baseStarId || layerMetadata?.baseStarId || ''
    };
  });

  // Track metadata hash to prevent infinite update loops
  const metadataHashRef = useRef<string>('');
  const computedHash = JSON.stringify(starMetadata);

  // 🔧 FIX: Use ref to track latest editableData for cleanup without causing re-renders
  const editableDataRef = useRef(editableData);
  useEffect(() => {
    editableDataRef.current = editableData;
  }, [editableData]);

  // Update editableData when starMetadata changes (from AI generation)
  React.useEffect(() => {
    if (computedHash !== metadataHashRef.current) {
      console.log(
        '🔄 [STARS INFO] starMetadata changed, updating editableData'
      );
      console.log(
        '🔍 [STARS INFO] Eye Color from starMetadata:',
        starMetadata?.physicalTraits?.eyeColor || starMetadata?.eyeColor
      );
      console.log(
        '🔍 [STARS INFO] Current Eye Color in editableData:',
        editableData.physicalTraits?.eyeColor
      );
      console.log(
        '🔍 [STARS INFO] Vibe from starMetadata:',
        starMetadata?.vibe
      );
      console.log(
        '🔍 [STARS INFO] Current Vibe in editableData:',
        editableData.vibe
      );
      metadataHashRef.current = computedHash;

      // CRITICAL FIX (Issue #15): Auto-populate description with AI-generated description on update
      // Pattern: AI description → creator description → empty string
      const updatedDescription =
        aiGeneratedDescription ||
        starMetadata?.creatorDescription ||
        starMetadata?.description ||
        '';
      if (aiGeneratedDescription && !starMetadata?.creatorDescription && !starMetadata?.description) {
        console.log('📝 [STARS INFO] Auto-populating Star Description with AI-generated description on update:', aiGeneratedDescription);
      }

      setEditableData({
        creatorDescription: updatedDescription,
        starName: starMetadata?.starName || '',
        realName: starMetadata?.realName || '',
        gender: coerceEnumValue(starMetadata?.gender, GENDER_VALUES),
        ageGroup: coerceEnumValue(
          starMetadata?.ageGroup || starMetadata?.age,
          AGE_GROUP_VALUES
        ),
        ethnicity: starMetadata?.ethnicity || '',
        physicalTraits: {
          bodyType:
            starMetadata?.physicalTraits?.bodyType ||
            starMetadata?.bodyType ||
            '',
          height:
            starMetadata?.physicalTraits?.height || starMetadata?.height || '',
          hairColor: (() => {
            // INTELLIGENT HAIR COLOR DETECTION: Auto-detect from tags when AI missed it
            const aiHairColor = starMetadata?.physicalTraits?.hairColor || starMetadata?.hairColor || '';
            const allTags = [
              ...(starMetadata?.tags || []),
              ...(asset?.tags || []),
              ...(asset?.aiMetadata?.tags || [])
            ];

            // If AI already detected a specific color, use it
            if (aiHairColor && aiHairColor !== 'Other' && aiHairColor !== '') {
              return aiHairColor;
            }

            // Auto-detect colorful hair from tags
            const hasColorfulHair = allTags.some(tag =>
              tag.toLowerCase().includes('colorful') ||
              tag.toLowerCase().includes('blonde-highlight') ||
              tag.toLowerCase().includes('highlighted-hair') ||
              tag.toLowerCase().includes('multi-color') ||
              tag.toLowerCase().includes('rainbow')
            );

            if (hasColorfulHair) {
              console.log('🎨 [HAIR COLOR] Auto-detected colorful hair from tags:', allTags.filter(tag =>
                tag.toLowerCase().includes('colorful') ||
                tag.toLowerCase().includes('highlight') ||
                tag.toLowerCase().includes('multi') ||
                tag.toLowerCase().includes('rainbow')
              ));
              return 'Colorful';
            }

            // Check for other specific colors in tags
            const hasBlonde = allTags.some(tag => tag.toLowerCase().includes('blonde') && !tag.toLowerCase().includes('highlight'));
            if (hasBlonde) return 'Blonde';

            const hasBrown = allTags.some(tag => tag.toLowerCase().includes('brown'));
            if (hasBrown) return 'Medium Brown';

            const hasAuburn = allTags.some(tag => tag.toLowerCase().includes('auburn'));
            if (hasAuburn) return 'Auburn';

            // Default to AI response or empty
            return aiHairColor || '';
          })(),
          hairLength:
            starMetadata?.physicalTraits?.hairLength ||
            starMetadata?.hairLength ||
            '',
          hairStyle:
            starMetadata?.physicalTraits?.hairStyle ||
            starMetadata?.hairStyle ||
            '',
          eyeColor: (() => {
            const rawEyeColor =
              starMetadata?.physicalTraits?.eyeColor ||
              starMetadata?.eyeColor ||
              '';
            // CRITICAL FIX: Normalize eye color values to match dropdown options
            const normalizedEyeColor = rawEyeColor
              ? rawEyeColor.charAt(0).toUpperCase() +
                rawEyeColor.slice(1).toLowerCase()
              : '';
            console.log(
              '🔍 [STARS INFO] Raw Eye Color from backend:',
              rawEyeColor
            );
            console.log(
              '🔍 [STARS INFO] Normalized Eye Color for UI:',
              normalizedEyeColor
            );

            // Validate against known options
            const validOptions = [
              'Brown',
              'Blue',
              'Green',
              'Hazel',
              'Gray',
              'Other',
            ];
            const finalEyeColor = validOptions.includes(normalizedEyeColor)
              ? normalizedEyeColor
              : rawEyeColor;
            console.log(
              '🔍 [STARS INFO] Final Eye Color after validation:',
              finalEyeColor
            );

            return finalEyeColor;
          })(),
          skinTone:
            starMetadata?.physicalTraits?.skinTone ||
            starMetadata?.skinTone ||
            '',
        },
        archetype: starMetadata?.archetype || '',
        musicalStyle: (() => {
          // 🎯 CRITICAL FIX: Use multi-source extraction (same as initialization)
          // Check for non-empty arrays, not just truthy (empty array [] is truthy!)
          let musicalStyleData =
            (starMetadata?.musicalStyle?.length > 0 ? starMetadata.musicalStyle : null) ||
            asset?.starMetadata?.musicalStyle ||
            asset?.aiMetadata?.layerMetadata?.musicalStyle ||
            asset?.musicalStyle ||
            asset?.aiMetadata?.starsMetadata?.musicalStyle ||
            [];

          const currentValue = Array.isArray(musicalStyleData)
            ? musicalStyleData
            : typeof musicalStyleData === 'string' && musicalStyleData
            ? musicalStyleData.split(',').map((s: string) => s.trim())
            : [];

          // If current value is empty, try to inherit from base asset
          if (
            currentValue.length === 0 &&
            baseAssetData?.starMetadata?.musicalStyle
          ) {
            const baseValue = baseAssetData.starMetadata.musicalStyle;
            const inheritedValue = Array.isArray(baseValue)
              ? baseValue
              : typeof baseValue === 'string' && baseValue
              ? baseValue.split(',').map((s: string) => s.trim())
              : [];
            console.log(
              '🔄 [INHERITANCE] musicalStyle: Inherited from base asset:',
              inheritedValue
            );
            return inheritedValue;
          }

          return currentValue;
        })(),
        careerStage: starMetadata?.careerStage || '',
        energy: coerceEnumValue(
          starMetadata?.energy || starMetadata?.energyLevel,
          INTENSITY_LEVEL_VALUES
        ),
        personalityTraits: (() => {
          // 🎯 CRITICAL FIX: Apply inheritance for personalityTraits with baseAsset fallback
          const currentValue = Array.isArray(starMetadata?.personalityTraits)
            ? starMetadata?.personalityTraits
            : typeof starMetadata?.personalityTraits === 'string' &&
              starMetadata?.personalityTraits
            ? starMetadata.personalityTraits
                .split(',')
                .map((t: string) => t.trim())
            : [];

          // If current value is empty, try to inherit from base asset
          if (
            currentValue.length === 0 &&
            baseAssetData?.starMetadata?.personalityTraits
          ) {
            const baseValue = baseAssetData.starMetadata.personalityTraits;
            const inheritedValue = Array.isArray(baseValue)
              ? baseValue
              : typeof baseValue === 'string' && baseValue
              ? baseValue.split(',').map((t: string) => t.trim())
              : [];
            console.log(
              '🔄 [INHERITANCE] personalityTraits: Inherited from base asset:',
              inheritedValue
            );
            return inheritedValue;
          }

          return currentValue;
        })(),
        vibe: starMetadata?.vibe || '',
        // AlgoRhythm Metadata (REQUIRED for recommendation engine)
        performanceContext: coerceEnumArray(
          starMetadata?.performanceContext,
          PERFORMANCE_CONTEXT_VALUES,
          STAR_METADATA.performanceContext.maxItems
        ),
        targetAudience: coerceEnumArray(
          starMetadata?.targetAudience,
          AGE_GROUP_VALUES,
          STAR_METADATA.targetAudience.maxItems
        ),
        // CRITICAL FIX: Add missing makeup fields that were causing Step 4 issues
        makeupType:
          starMetadata?.makeupType || starMetadata?.makeupStyle?.type || '',
        colorPalette: coerceEnumValue(
          starMetadata?.colorPalette || starMetadata?.makeupStyle?.palette,
          COLOR_PALETTE_VALUES
        ),
        // ✨ Enhanced Hair & Makeup Fields
        hairTexture: starMetadata?.hairTexture || '',
        hairTreatment: starMetadata?.hairTreatment || '',
        hairAccessories: starMetadata?.hairAccessories || '',
        eyeMakeup: starMetadata?.eyeMakeup || '',
        lipMakeup: (() => {
          // 🎯 CRITICAL FIX: Apply inheritance for lipMakeup with baseAsset fallback
          const currentValue = starMetadata?.lipMakeup || '';

          // If current value is empty, try to inherit from base asset
          if (!currentValue && baseAssetData?.starMetadata?.lipMakeup) {
            const inheritedValue = baseAssetData.starMetadata.lipMakeup;
            console.log(
              '🔄 [INHERITANCE] lipMakeup: Inherited from base asset:',
              inheritedValue
            );
            return inheritedValue;
          }

          return currentValue;
        })(),
        faceMakeup: starMetadata?.faceMakeup || '',
        makeupEffects: starMetadata?.makeupEffects || '',
        assetType: starMetadata?.assetType || editableData?.assetType || 'base',
        baseStarId: starMetadata?.baseStarId || editableData?.baseStarId || '',
      });
    }
  }, [computedHash, starMetadata, aiGeneratedDescription]);

  // Debounce timeouts for field changes
  const debounceTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});

  // Enhanced field change handler with debouncing (following LooksInformation pattern)
  const handleFieldChange = useCallback(
    (field: string, value: string | string[]) => {
      if (field === 'physicalTraits.eyeColor') {
        console.log('👁️ [STARS INFO] User changing eye color to:', value);
      }

      if (field === 'vibe') {
        console.log('✨ [STARS INFO] User changing vibe to:', value);
      }

      if (field.startsWith('physicalTraits.')) {
        const traitKey = field.split('.')[1];
        setEditableData(prev => ({
          ...prev,
          physicalTraits: {
            ...prev.physicalTraits,
            [traitKey]: value,
          },
        }));
      } else {
        setEditableData(prev => ({ ...prev, [field]: value }));
      }

      if (debounceTimeouts.current[field]) {
        clearTimeout(debounceTimeouts.current[field]);
      }

      debounceTimeouts.current[field] = setTimeout(() => {
        if (onMetadataChange) {
          setEditableData(current => {
            const processedData = {
              ...layerMetadata, // Start with all required V2.0 fields
              ...current, // Override with edited values
            };
            processedData.gender = coerceEnumValue(
              processedData.gender,
              GENDER_VALUES
            );
            processedData.ageGroup = coerceEnumValue(
              processedData.ageGroup,
              AGE_GROUP_VALUES
            );
            processedData.energy = coerceEnumValue(
              processedData.energy,
              INTENSITY_LEVEL_VALUES
            );
            processedData.colorPalette = coerceEnumValue(
              processedData.colorPalette,
              COLOR_PALETTE_VALUES
            );
            processedData.performanceContext = coerceEnumArray(
              processedData.performanceContext,
              PERFORMANCE_CONTEXT_VALUES,
              STAR_METADATA.performanceContext.maxItems
            );
            processedData.targetAudience = coerceEnumArray(
              processedData.targetAudience,
              AGE_GROUP_VALUES,
              STAR_METADATA.targetAudience.maxItems
            );
            onMetadataChange(processedData);
            return current;
          });
        }
      }, 500);
    },
    [onMetadataChange]
  );

  // Cleanup timeouts on unmount AND flush pending changes
  // 🔧 FIX: Use empty dependency array to only run on unmount, use ref for latest data
  React.useEffect(() => {
    return () => {
      // CRITICAL FIX: Flush all pending debounced changes before unmount
      // This ensures user edits in Review & Submit step are not lost
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });

      // Immediately trigger onMetadataChange with latest editableData from ref
      if (onMetadataChange) {
        const processedData = {
          ...layerMetadata,
          ...editableDataRef.current,
        };
        onMetadataChange(processedData);
        console.log('💾 [STARS METADATA] Flushing pending changes on unmount:', processedData);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoized dropdown options to prevent unnecessary re-renders
  const genderOptions = useMemo(
    () => buildEnumOptions(GENDER_VALUES, genderLabels),
    []
  );

  const ageOptions = useMemo(
    () => buildEnumOptions(AGE_GROUP_VALUES, ageGroupLabels),
    []
  );

  const archetypeOptions = useMemo(
    () => ['Emerging', 'Rising', 'Established', 'Legendary'],
    []
  );

  const careerStageOptions = useMemo(
    () => [
      'New',
      'Breakthrough',
      'Rising',
      'Peak',
      'Established',
      'Veteran',
      'Legacy',
    ],
    []
  );

  const energyLevelOptions = useMemo(
    () => buildEnumOptions(INTENSITY_LEVEL_VALUES, intensityLevelLabels),
    []
  );

  const bodyTypeOptions = useMemo(
    () => ['Slim', 'Athletic', 'Average', 'Curvy', 'Plus Size'],
    []
  );

  const heightOptions = useMemo(
    () => ['Short', 'Average', 'Tall', 'Very Tall'],
    []
  );

  // 🎨 Enhanced Hair Color Options - Natural, Fashion, and Special Effects
  const hairColorOptions = useMemo(
    () => [
      // Natural Colors
      'Black',
      'Dark Brown',
      'Medium Brown',
      'Light Brown',
      'Auburn',
      'Strawberry Blonde',
      'Dirty Blonde',
      'Platinum Blonde',
      'Ash Blonde',
      'Golden Blonde',
      'Red',
      'Copper',
      'Burgundy',
      'Gray',
      'Silver',
      'White',
      // Fashion Colors
      'Blue',
      'Teal',
      'Green',
      'Purple',
      'Violet',
      'Pink',
      'Rose Gold',
      'Coral',
      'Orange',
      'Yellow',
      'Rainbow',
      'Ombré',
      'Balayage',
      // Special Effects
      'Highlighted',
      'Lowlighted',
      'Streaked',
      'Dip-Dyed',
      'Color-Blocked',
      'Two-Toned',
      'Gradient',
      'Peekaboo',
      'Colorful',
      'Other',
    ],
    []
  );

  const hairLengthOptions = useMemo(
    () => ['Bald', 'Very Short', 'Short', 'Medium', 'Long', 'Very Long'],
    []
  );

  const eyeColorOptions = useMemo(
    () => ['Brown', 'Blue', 'Green', 'Hazel', 'Gray', 'Other'],
    []
  );

  const vibeOptions = useMemo(
    () => [
      'Energetic',
      'Calm',
      'Mysterious',
      'Romantic',
      'Dramatic',
      'Playful',
      'Intense',
      'Peaceful',
      'Inspiring',
      'Other',
    ],
    []
  );

  // Additional dropdown options requested by user
  const ethnicityOptions = useMemo(
    () => [
      'Caucasian',
      'African American',
      'Hispanic/Latino',
      'Asian',
      'Native American',
      'Middle Eastern',
      'Pacific Islander',
      'Mixed/Multiracial',
      'Other',
    ],
    []
  );

  // 🎨 Enhanced Hair Style Options - Textures, Styles, and Techniques
  const hairStyleOptions = useMemo(
    () => [
      // Basic Textures
      'Straight',
      'Wavy',
      'Loose Curls',
      'Tight Curls',
      'Coily',
      'Kinky',
      'Crimped',
      'Spiral Curls',
      // Styling Techniques
      'Natural',
      'Sleek',
      'Tousled',
      'Beachy Waves',
      'Hollywood Glam',
      'Messy',
      'Textured',
      'Voluminous',
      'Blown Out',
      'Finger-Waved',
      'Pin Curls',
      'Braided Texture',
      'Heat-Styled',
      'Air-Dried',
      'Scrunched',
      // Special Effects and Treatments (KEY FOR TESTING!)
      'Highlighted',
      'Lowlighted',
      'Balayage',
      'Ombré',
      'Dip-Dyed',
      'Color-Blocked',
      // Classic Styles
      'Braided',
      'Updo',
      'Ponytail',
      'Bun',
      'Loose',
      'Pixie Cut',
      'Bob',
      'Layers',
      'Afro',
      'Dreadlocks',
      'Buzz Cut',
      'Shag',
      'Feathered',
      'Choppy',
      'Face-Framing',
      'Undercut',
      'Side-Swept',
      'Other',
    ],
    []
  );

  const skinToneOptions = useMemo(
    () => [
      'Fair',
      'Light',
      'Medium',
      'Olive',
      'Tan',
      'Deep',
      'Dark',
      'Very Dark',
      'Other',
    ],
    []
  );

  // 💄 Enhanced Makeup Type Options - Intensity and Style Categories
  const makeupTypeOptions = useMemo(
    () => [
      // Intensity Levels
      'No Makeup',
      'Natural',
      'Minimal',
      'Moderate',
      'Full Glam',
      'Editorial',
      'Avant-Garde',
      // Style Categories
      'Natural Glow',
      'Dewy',
      'Matte',
      'Smoky',
      'Classic Hollywood',
      'Pin-Up',
      'Gothic',
      'Bohemian',
      'Ethereal',
      'Dramatic',
      'Subtle Enhancement',
      'Stage',
      'Bridal',
      'Festival',
      'Artistic',
      'Character',
      'Vintage',
      'Modern',
      'Minimalist',
      'Everyday',
      'Glamour',
      'Glamorous',
    ],
    []
  );

  // 🎨 Enhanced Color Palette Options
  const colorPaletteOptions = useMemo(
    () => buildEnumOptions(COLOR_PALETTE_VALUES, colorPaletteLabels),
    []
  );

  const performanceContextOptions = useMemo(
    () => buildEnumOptions(PERFORMANCE_CONTEXT_VALUES, performanceContextLabels),
    []
  );

  // 🎨 Hair Texture Options - Professional 1A-4C System
  const hairTextureOptions = useMemo(
    () => [
      'None',
      // Fine Hair
      '1A (Straight, Fine)',
      '1B (Straight, Medium)',
      '1C (Straight, Coarse)',
      // Wavy Hair
      '2A (Wavy, Fine)',
      '2B (Wavy, Medium)',
      '2C (Wavy, Coarse)',
      // Curly Hair
      '3A (Curly, Loose)',
      '3B (Curly, Springy)',
      '3C (Curly, Tight)',
      // Coily/Kinky Hair
      '4A (Coily, Soft)',
      '4B (Coily, Wiry)',
      '4C (Coily, Very Tight)',
      // Special Textures
      'Baby Fine',
      'Thick',
      'Wiry',
      'Silky',
      'Coarse',
      'Damaged',
      'Chemically Treated',
      'Virgin Hair',
      'Extensions',
      'Other',
    ],
    []
  );

  // 🎨 Hair Treatment Options - Chemical, Color, and Styling Treatments
  const hairTreatmentOptions = useMemo(
    () => [
      'None',
      // Color Treatments
      'Highlights',
      'Lowlights',
      'Full Color',
      'Root Touch-up',
      'Balayage',
      'Ombré',
      'Color Correction',
      'Bleached',
      'Toned',
      'Semi-Permanent',
      'Temporary Color',
      // Chemical Treatments
      'Keratin Treatment',
      'Brazilian Blowout',
      'Chemical Straightening',
      'Relaxer',
      'Perm',
      'Japanese Straightening',
      'Protein Treatment',
      'Deep Conditioning',
      // Styling Treatments
      'Heat Protection',
      'Volume Treatment',
      'Smoothing Treatment',
      'Curl Enhancer',
      'Frizz Control',
      'Shine Treatment',
      'UV Protection',
      'Color Protection',
      // Natural Treatments
      'Oil Treatment',
      'Mask Treatment',
      'Natural Conditioning',
      'Scalp Treatment',
      'Other',
    ],
    []
  );

  // 🎨 Hair Accessories Options - Extensions, Clips, and Embellishments
  const hairAccessoriesOptions = useMemo(
    () => [
      'None',
      // Extensions and Add-ons
      'Hair Extensions',
      'Clip-In Extensions',
      'Tape Extensions',
      'Weave',
      'Wig',
      'Hairpiece',
      // Basic Accessories
      'Headband',
      'Hair Clips',
      'Bobby Pins',
      'Hair Ties',
      'Scrunchie',
      'Hair Elastic',
      'Barrettes',
      'Hair Comb',
      'Hair Brush Styling',
      // Decorative Elements
      'Hair Ribbon',
      'Silk Scarf',
      'Flowers',
      'Hair Jewels',
      'Hair Gems',
      'Feathers',
      'Hair Chain',
      'Hair Vine',
      'Hair Crown',
      'Hair Tiara',
      // Special Effects
      'Glitter',
      'Hair Tinsel',
      'Temporary Tattoos',
      'Hair Chalk',
      'Spray Color',
      'Hair Stickers',
      'Holographic Elements',
      'Other',
    ],
    []
  );

  // 💄 Eye Makeup Options - Complete Eye Makeup System
  const eyeMakeupOptions = useMemo(
    () => [
      'None',
      'Natural Eyes',
      'Minimal Eyes',
      // Eyeshadow Styles
      'Neutral Shadow',
      'Earth Tones',
      'Bold Colors',
      'Metallic Shadow',
      'Glitter Shadow',
      'Smoky Eyes',
      'Cut Crease',
      'Halo Eye',
      'Gradient Shadow',
      'Monochromatic',
      // Eyeliner Styles
      'Thin Line',
      'Winged Eyeliner',
      'Bold Wing',
      'Cat Eye',
      'Smoky Liner',
      'Graphic Liner',
      'Colored Liner',
      'Double Line',
      'Floating Liner',
      'Under-eye Liner',
      // Lash Styles
      'Natural Lashes',
      'Mascara Only',
      'False Lashes',
      'Individual Lashes',
      'Dramatic Volume',
      'Length Focus',
      'Bottom Lashes',
      'Colored Mascara',
      // Brow Styles
      'Natural Brows',
      'Defined Brows',
      'Bold Brows',
      'Feathered Brows',
      'Sculpted Brows',
      'Glossy Brows',
      'Colored Brows',
      'Laminated Brows',
    ],
    []
  );

  // 💄 Lip Makeup Options - Color, Finish, and Style
  const lipMakeupOptions = useMemo(
    () => [
      'None',
      'Natural Lips',
      'Lip Balm',
      // Colors
      'Nude Lips',
      'Pink Lips',
      'Red Lips',
      'Berry Lips',
      'Coral Lips',
      'Brown Lips',
      'Bold Color Lips',
      'Metallic Lips',
      'Dark Lips',
      'Bright Lips',
      // Finishes
      'Matte Lips',
      'Satin Lips',
      'Glossy Lips',
      'Shimmery Lips',
      'Velvet Lips',
      'Stained Lips',
      'Lacquered Lips',
      'Vinyl Lips',
      // Styles
      'Overlined Lips',
      'Gradient Lips',
      'Ombré Lips',
      'Minimalist Lips',
      'Statement Lips',
      'Vintage Lips',
      'Editorial Lips',
    ],
    []
  );

  // 💄 Face Makeup Options - Foundation, Contouring, Highlighting
  const faceMakeupOptions = useMemo(
    () => [
      'No Foundation',
      'Minimal Coverage',
      'Light Coverage',
      'Medium Coverage',
      'Full Coverage',
      'Airbrushed',
      'Natural Finish',
      'Matte Finish',
      'Dewy Finish',
      // Contouring
      'No Contouring',
      'Subtle Contour',
      'Defined Contour',
      'Dramatic Contour',
      'Natural Shadow',
      'Sculpted Face',
      'Chiseled Look',
      // Highlighting
      'No Highlight',
      'Subtle Glow',
      'Moderate Highlight',
      'Intense Glow',
      'Strobing',
      'Metallic Highlight',
      'Holographic Highlight',
      // Blush
      'No Blush',
      'Natural Flush',
      'Rosy Cheeks',
      'Peachy Blush',
      'Berry Blush',
      'Dramatic Blush',
      'Draping Technique',
      'Sunset Blush',
    ],
    []
  );

  // 💄 Makeup Effects Options - Special Effects and Themes
  const makeupEffectsOptions = useMemo(
    () => [
      'No Effects',
      // Special Effects
      'Glitter',
      'Rhinestones',
      'Face Gems',
      'Body Paint',
      'Temporary Tattoos',
      'Face Stickers',
      'Metallics',
      'Holographic',
      'Color-Changing',
      'Glow-in-Dark',
      'UV Reactive',
      'Chrome Effect',
      'Oil Slick',
      'Iridescent',
      // Themes
      'Festival Makeup',
      'Editorial Effects',
      'Avant-Garde',
      'Fantasy Makeup',
      'Sci-Fi',
      'Vintage Effects',
      'Futuristic',
      'Artistic Paint',
      'Character Makeup',
      'Costume Effects',
      'Tribal Patterns',
      'Geometric Designs',
      'Abstract Art',
      'Nature-Inspired',
    ],
    []
  );

  // Don't render if no metadata available
  if (
    !starMetadata &&
    !asset.starsMetadata &&
    !asset.aiMetadata?.starsMetadata
  ) {
    return null;
  }

  return (
    <Box sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <StarIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Star Metadata
        </Typography>
      </Box>

      <Grid container spacing={twoColumnLayout ? 3 : 2}>
        {/* Core Star Information */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Core Information
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              required
              fullWidth
              label="Star Name"
              value={getDisplayValue('starName') || editableData.starName}
              onChange={e => handleFieldChange('starName', e.target.value)}
              size="small"
              inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
              error={!editableData.starName || editableData.starName.trim() === ''}
              // Show inheritance indicator if value is inherited
              helperText={
                isInheritedValue('starName')
                  ? '📥 Inherited from base asset'
                  : (!editableData.starName || editableData.starName.trim() === '')
                  ? 'Star name is required'
                  : `${API_STRING_MAX_LENGTH} characters max`
              }
              sx={
                isInheritedValue('starName')
                  ? {
                      '& .MuiInputLabel-root': { color: 'info.main' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'info.main' },
                      },
                    }
                  : undefined
              }
            />
          ) : (
            <Typography variant="body2">
              <strong>Star Name:</strong>{' '}
              {getDisplayValue('starName') ||
                editableData.starName ||
                'Not specified'}
              {isInheritedValue('starName') && (
                <Chip
                  label="📥 Inherited"
                  size="small"
                  variant="outlined"
                  color="info"
                  sx={{ ml: 1, fontSize: '0.7rem' }}
                />
              )}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Real Name"
              value={editableData.realName}
              onChange={e => handleFieldChange('realName', e.target.value)}
              size="small"
              inputProps={{ maxLength: API_STRING_MAX_LENGTH }}
            />
          ) : (
            <Typography variant="body2">
              <strong>Real Name:</strong>{' '}
              {editableData.realName || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              options={genderOptions}
              value={genderOptions.find(option => option.value === editableData.gender) ?? null}
              onChange={(event, newValue) =>
                handleFieldChange('gender', newValue?.value || '')
              }
              renderInput={params => (
                <TextField 
                  {...params} 
                  label="Gender" 
                  required
                  size="small"
                  error={!editableData.gender || editableData.gender.trim() === ''}
                  helperText={!editableData.gender || editableData.gender.trim() === '' ? 'Gender is required' : ''}
                />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Typography variant="body2">
              <strong>Gender:</strong> {editableData.gender ? genderLabels[editableData.gender as Gender] : 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              options={ageOptions}
              value={ageOptions.find(option => option.value === editableData.ageGroup) ?? null}
              onChange={(event, newValue) =>
                handleFieldChange('ageGroup', newValue?.value || '')
              }
              renderInput={params => (
                <TextField 
                  {...params} 
                  label="Age Group" 
                  required
                  size="small"
                  error={!editableData.ageGroup || editableData.ageGroup.trim() === ''}
                  helperText={!editableData.ageGroup || editableData.ageGroup.trim() === '' ? 'Age group is required' : ''}
                />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Typography variant="body2">
              <strong>Age Group:</strong>{' '}
              {editableData.ageGroup ? ageGroupLabels[editableData.ageGroup as AgeGroup] : 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={ethnicityOptions}
              value={editableData.ethnicity}
              inputValue={editableData.ethnicity}
              onInputChange={(event, newValue) =>
                handleFieldChange('ethnicity', newValue || '')
              }
              renderInput={params => (
                <TextField 
                  {...params} 
                  label="Ethnicity" 
                  required
                  size="small"
                  error={!editableData.ethnicity || editableData.ethnicity.trim() === ''}
                  helperText={!editableData.ethnicity || editableData.ethnicity.trim() === '' ? 'Ethnicity is required' : ''}
                />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Ethnicity:</strong>{' '}
              {editableData.ethnicity || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Physical Traits */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Physical Traits
          </Typography>
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={bodyTypeOptions}
              value={editableData.physicalTraits.bodyType}
              inputValue={editableData.physicalTraits.bodyType}
              onInputChange={(event, newValue) =>
                handleFieldChange('physicalTraits.bodyType', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Body Type" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Body Type:</strong>{' '}
              {editableData.physicalTraits.bodyType || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={heightOptions}
              value={editableData.physicalTraits.height}
              inputValue={editableData.physicalTraits.height}
              onInputChange={(event, newValue) =>
                handleFieldChange('physicalTraits.height', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Height" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Height:</strong>{' '}
              {editableData.physicalTraits.height || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={hairColorOptions}
              value={editableData.physicalTraits.hairColor}
              inputValue={editableData.physicalTraits.hairColor}
              onInputChange={(event, newValue) =>
                handleFieldChange('physicalTraits.hairColor', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Hair Color" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Hair Color:</strong>{' '}
              {editableData.physicalTraits.hairColor || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={hairLengthOptions}
              value={editableData.physicalTraits.hairLength}
              inputValue={editableData.physicalTraits.hairLength}
              onInputChange={(event, newValue) =>
                handleFieldChange('physicalTraits.hairLength', newValue || '')
              }
              renderInput={params => (
                <TextField 
                  {...params} 
                  label="Hair Length" 
                  size="small"
                />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Hair Length:</strong>{' '}
              {editableData.physicalTraits.hairLength || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={hairStyleOptions}
              value={editableData.physicalTraits.hairStyle}
              inputValue={editableData.physicalTraits.hairStyle}
              onInputChange={(event, newValue) =>
                handleFieldChange('physicalTraits.hairStyle', newValue || '')
              }
              renderInput={params => (
                <TextField 
                  {...params} 
                  label="Hair Style" 
                  size="small"
                />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Hair Style:</strong>{' '}
              {editableData.physicalTraits.hairStyle || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={eyeColorOptions}
              value={editableData.physicalTraits.eyeColor}
              inputValue={editableData.physicalTraits.eyeColor}
              onInputChange={(event, newValue) =>
                handleFieldChange('physicalTraits.eyeColor', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Eye Color" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Eye Color:</strong>{' '}
              {editableData.physicalTraits.eyeColor || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={skinToneOptions}
              value={
                getDisplayValue('physicalTraits.skinTone') ||
                editableData.physicalTraits.skinTone
              }
              inputValue={
                getDisplayValue('physicalTraits.skinTone') ||
                editableData.physicalTraits.skinTone
              }
              onInputChange={(event, newValue) =>
                handleFieldChange('physicalTraits.skinTone', newValue || '')
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Skin Tone"
                  size="small"
                  // Show inheritance indicator if value is inherited
                  helperText={
                    isInheritedValue('physicalTraits.skinTone')
                      ? '📥 Inherited from base asset'
                      : undefined
                  }
                />
              )}
              sx={
                isInheritedValue('physicalTraits.skinTone')
                  ? {
                      '& .MuiInputLabel-root': { color: 'info.main' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'info.main' },
                      },
                    }
                  : undefined
              }
            />
          ) : (
            <Typography variant="body2">
              <strong>Skin Tone:</strong>{' '}
              {getDisplayValue('physicalTraits.skinTone') ||
                editableData.physicalTraits.skinTone ||
                'Not specified'}
              {isInheritedValue('physicalTraits.skinTone') && (
                <Chip
                  label="📥 Inherited"
                  size="small"
                  variant="outlined"
                  color="info"
                  sx={{ ml: 1, fontSize: '0.7rem' }}
                />
              )}
            </Typography>
          )}
        </Grid>

        {/* ✨ ENHANCED HAIR FIELDS */}
        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={hairTextureOptions}
              value={editableData.hairTexture}
              inputValue={editableData.hairTexture}
              onInputChange={(event, newValue) =>
                handleFieldChange('hairTexture', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Hair Texture" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Hair Texture:</strong>{' '}
              {editableData.hairTexture || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={hairTreatmentOptions}
              value={editableData.hairTreatment}
              inputValue={editableData.hairTreatment}
              onInputChange={(event, newValue) =>
                handleFieldChange('hairTreatment', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Hair Treatment" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Hair Treatment:</strong>{' '}
              {editableData.hairTreatment || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={hairAccessoriesOptions}
              value={editableData.hairAccessories}
              inputValue={editableData.hairAccessories}
              onInputChange={(event, newValue) =>
                handleFieldChange('hairAccessories', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Hair Accessories" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Hair Accessories:</strong>{' '}
              {editableData.hairAccessories || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Makeup Information - CRITICAL FIX: Add missing makeup section */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            💄 Makeup Style
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={makeupTypeOptions}
              value={editableData.makeupType}
              inputValue={editableData.makeupType}
              onInputChange={(event, newValue) =>
                handleFieldChange('makeupType', newValue || '')
              }
              renderInput={params => (
                <TextField 
                  {...params} 
                  label="Makeup Type" 
                  size="small"
                />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Makeup Type:</strong>{' '}
              {editableData.makeupType || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              options={colorPaletteOptions}
              value={colorPaletteOptions.find(option => option.value === editableData.colorPalette) ?? null}
              onChange={(event, newValue) =>
                handleFieldChange('colorPalette', newValue?.value || '')
              }
              renderInput={params => (
                <TextField 
                  {...params} 
                  label="Color Palette" 
                  size="small"
                />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Typography variant="body2">
              <strong>Color Palette:</strong>{' '}
              {editableData.colorPalette ? colorPaletteLabels[editableData.colorPalette as ColorPalette] : 'Not specified'}
            </Typography>
          )}
        </Grid>

        {/* ✨ ENHANCED MAKEUP FIELDS */}
        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={eyeMakeupOptions}
              value={editableData.eyeMakeup}
              inputValue={editableData.eyeMakeup}
              onInputChange={(event, newValue) =>
                handleFieldChange('eyeMakeup', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Eye Makeup" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Eye Makeup:</strong>{' '}
              {editableData.eyeMakeup || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={lipMakeupOptions}
              value={editableData.lipMakeup}
              inputValue={editableData.lipMakeup}
              onInputChange={(event, newValue) =>
                handleFieldChange('lipMakeup', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Lip Makeup" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Lip Makeup:</strong>{' '}
              {editableData.lipMakeup || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={faceMakeupOptions}
              value={editableData.faceMakeup}
              inputValue={editableData.faceMakeup}
              onInputChange={(event, newValue) =>
                handleFieldChange('faceMakeup', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Face Makeup" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Face Makeup:</strong>{' '}
              {editableData.faceMakeup || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={makeupEffectsOptions}
              value={editableData.makeupEffects}
              inputValue={editableData.makeupEffects}
              onInputChange={(event, newValue) =>
                handleFieldChange('makeupEffects', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Makeup Effects" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Makeup Effects:</strong>{' '}
              {editableData.makeupEffects || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Career Information */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Career & Personality
          </Typography>
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={archetypeOptions}
              value={editableData.archetype}
              inputValue={editableData.archetype}
              onInputChange={(event, newValue) =>
                handleFieldChange('archetype', newValue || '')
              }
              renderInput={params => (
                <TextField 
                  {...params} 
                  label="Archetype" 
                  size="small"
                />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Archetype:</strong>{' '}
              {editableData.archetype || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={careerStageOptions}
              value={editableData.careerStage}
              inputValue={editableData.careerStage}
              onInputChange={(event, newValue) =>
                handleFieldChange('careerStage', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Career Stage" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Career Stage:</strong>{' '}
              {editableData.careerStage || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              options={energyLevelOptions}
              value={energyLevelOptions.find(option => option.value === editableData.energy) ?? null}
              onChange={(event, newValue) =>
                handleFieldChange('energy', newValue?.value || '')
              }
              renderInput={params => (
                <TextField 
                  {...params} 
                  label="Energy Level" 
                  size="small"
                />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              getOptionLabel={option => option.label}
            />
          ) : (
            <Typography variant="body2">
              <strong>Energy Level:</strong>{' '}
              {editableData.energy ? intensityLevelLabels[editableData.energy as IntensityLevel] : 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={vibeOptions}
              value={editableData.vibe}
              inputValue={editableData.vibe}
              onInputChange={(event, newValue) =>
                handleFieldChange('vibe', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Vibe" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Vibe:</strong> {editableData.vibe || 'Not specified'}
            </Typography>
          )}
        </Grid>

        {/* Musical Style and Personality Traits */}
        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              freeSolo
              options={[
                'Pop',
                'Rock',
                'Hip-Hop',
                'R&B',
                'Country',
                'Jazz',
                'Electronic',
              ]}
              value={(editableData.musicalStyle || []).slice(0, STAR_METADATA.musicalStyle.maxItems)}
              onChange={(event, newValue) =>
                handleFieldChange('musicalStyle', (newValue ?? []).slice(0, STAR_METADATA.musicalStyle.maxItems))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Musical Style"
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  helperText={`Max ${STAR_METADATA.musicalStyle.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Musical Style:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.musicalStyle || []).map((item: string, index: number) => (
                  <Chip key={index} label={item} size="small" variant="outlined" />
                ))}
                {(!editableData.musicalStyle?.length) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              multiple
              fullWidth
              freeSolo
              options={[
                'Confident',
                'Creative',
                'Charismatic',
                'Energetic',
                'Professional',
                'Artistic',
              ]}
              value={(editableData.personalityTraits || []).slice(0, STAR_METADATA.personalityTraits.maxItems)}
              onChange={(event, newValue) =>
                handleFieldChange('personalityTraits', (newValue ?? []).slice(0, STAR_METADATA.personalityTraits.maxItems))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Personality Traits"
                  size="small"
                  inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                  helperText={`Max ${STAR_METADATA.personalityTraits.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Personality Traits:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(editableData.personalityTraits || []).map((item: string, index: number) => (
                  <Chip key={index} label={item} size="small" variant="outlined" />
                ))}
                {(!editableData.personalityTraits?.length) && (
                  <Typography variant="body2" color="text.secondary">Not specified</Typography>
                )}
              </Box>
            </Box>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 3 }} />

        {/* ========== ALGORHYTHM METADATA SECTION ========== */}
        <Grid item xs={12}>
          <Box sx={{ p: 3, backgroundColor: 'background.paper', borderRadius: 2, border: '2px solid', borderColor: 'primary.main' }}>
            <Typography variant="h6" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
              🎯 AlgoRhythm Metadata
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              These fields help the recommendation engine suggest compatible Songs, Looks, Moves, and Worlds for this Star.
            </Typography>

            <Grid container spacing={2}>
              {/* Performance Context - Multi-select Autocomplete */}
              <Grid item xs={12} md={6}>
                {isEditable ? (
                  <Autocomplete
                    multiple
                    options={performanceContextOptions}
                    value={performanceContextOptions.filter(option =>
                      (editableData.performanceContext || []).includes(option.value)
                    )}
                    onChange={(event, newValue) =>
                      handleFieldChange(
                        'performanceContext',
                        newValue
                          .map(option => option.value)
                          .slice(0, STAR_METADATA.performanceContext.maxItems)
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Performance Context *"
                        inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                        helperText={`Where does this star typically perform? Max ${STAR_METADATA.performanceContext.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`}
                        error={!editableData.performanceContext || editableData.performanceContext.length === 0}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.label}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    getOptionLabel={option => option.label}
                  />
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Performance Context:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(editableData.performanceContext || []).map((context: PerformanceContext, index: number) => (
                        <Chip key={index} label={performanceContextLabels[context]} size="small" variant="outlined" />
                      ))}
                      {(!editableData.performanceContext || editableData.performanceContext.length === 0) && (
                        <Typography variant="body2" color="text.secondary">Not specified</Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Target Audience - Multi-select Autocomplete */}
              <Grid item xs={12} md={6}>
                {isEditable ? (
                  <Autocomplete
                    multiple
                    options={ageOptions}
                    value={ageOptions.filter(option =>
                      (editableData.targetAudience || []).includes(option.value)
                    )}
                    onChange={(event, newValue) =>
                      handleFieldChange(
                        'targetAudience',
                        newValue
                          .map(option => option.value)
                          .slice(0, STAR_METADATA.targetAudience.maxItems)
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Target Audience *"
                        inputProps={{ ...params.inputProps, maxLength: API_STRING_MAX_LENGTH }}
                        helperText={`Who is this star's primary audience? Required. Max ${STAR_METADATA.targetAudience.maxItems} items, ${API_STRING_MAX_LENGTH} chars each`}
                        error={!editableData.targetAudience || editableData.targetAudience.length === 0}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option.label}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    getOptionLabel={option => option.label}
                  />
                ) : (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      Target Audience:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(editableData.targetAudience || []).map((audience: AgeGroup, index: number) => (
                        <Chip key={index} label={ageGroupLabels[audience]} size="small" variant="outlined" />
                      ))}
                      {(!editableData.targetAudience || editableData.targetAudience.length === 0) && (
                        <Typography variant="body2" color="text.secondary">Not specified</Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Info Alert */}
              <Grid item xs={12}>
                <Box sx={{ p: 2, backgroundColor: 'info.light', borderRadius: 1, mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    💡 Why These Fields Matter
                  </Typography>
                  <Typography variant="caption">
                    AlgoRhythm uses these fields to recommend compatible content. For example, a star with "Concert" + "Young Adults" will be matched with energetic songs and dynamic moves.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StarsMetadataEditor;
